import type { LevelDefinition } from "@/domain";

export interface StorySequenceItem {
  id: string;
  label: string;
}

export interface StorySequencePayload {
  narration: string;
  narrationAudio: string;
  items: StorySequenceItem[];
  correctOrder: string[];
  hint: string;
}

export const storySequenceLevels: LevelDefinition<StorySequencePayload>[] = [
  {
    id: "chinese-story-sequence-1",
    domain: "chinese",
    activityType: "story-sequence",
    difficulty: 1,
    objectives: ["听懂两件事的先后", "按生活中的自然顺序排列"],
    title: "洗苹果",
    instruction: "小故事讲完啦，卡片悄悄乱了。听一听，把它们排好。",
    promptAudio: "/audio/chinese/story-sequence/1.ogg",
    assets: [],
    payload: {
      narration: "小禾捧着一个红苹果，肚子咕咕叫。她先把苹果洗干净，再开心地咬一口。",
      narrationAudio: "/audio/chinese/story-sequence/narration-1.ogg",
      items: [
        { id: "eat-apple", label: "吃苹果" },
        { id: "wash-apple", label: "洗干净苹果" }
      ],
      correctOrder: ["wash-apple", "eat-apple"],
      hint: "苹果要先洗个干净的小澡，还是先咬一口呢？"
    }
  },
  {
    id: "chinese-story-sequence-2",
    domain: "chinese",
    activityType: "story-sequence",
    difficulty: 2,
    objectives: ["记住三个早晨动作", "理解“先、然后、最后”"],
    title: "早晨出门",
    instruction: "小故事讲完啦，卡片悄悄乱了。听一听，把它们排好。",
    promptAudio: "/audio/chinese/story-sequence/2.ogg",
    assets: [],
    payload: {
      narration: "清晨，小安伸个懒腰起床啦。他先穿好衣服，再穿上鞋，开开心心出门。",
      narrationAudio: "/audio/chinese/story-sequence/narration-2.ogg",
      items: [
        { id: "put-on-shoes", label: "穿鞋" },
        { id: "get-up", label: "起床" },
        { id: "get-dressed", label: "穿好衣服" }
      ],
      correctOrder: ["get-up", "get-dressed", "put-on-shoes"],
      hint: "故事一开始，小安还在床上呢。哪张卡片最先出场？"
    }
  },
  {
    id: "chinese-story-sequence-3",
    domain: "chinese",
    activityType: "story-sequence",
    difficulty: 3,
    objectives: ["理解植物生长的简单顺序", "从故事中找出三个关键步骤"],
    title: "小豆子发芽",
    instruction: "小故事讲完啦，卡片悄悄乱了。听一听，把它们排好。",
    promptAudio: "/audio/chinese/story-sequence/3.ogg",
    assets: [],
    payload: {
      narration: "乐乐把一颗小豆子轻轻放进土里，又给它喝了点水。过了几天，嫩嫩的小芽探出头来。",
      narrationAudio: "/audio/chinese/story-sequence/narration-3.ogg",
      items: [
        { id: "seed-sprouts", label: "小芽钻出泥土" },
        { id: "plant-seed", label: "把豆子种进土里" },
        { id: "water-seed", label: "给豆子浇水" }
      ],
      correctOrder: ["plant-seed", "water-seed", "seed-sprouts"],
      hint: "小芽探头以前，小豆子要先住进土里。"
    }
  },
  {
    id: "chinese-story-sequence-4",
    domain: "chinese",
    activityType: "story-sequence",
    difficulty: 4,
    objectives: ["听记四个连续动作", "按校园生活的顺序整理故事"],
    title: "到教室啦",
    instruction: "小故事讲完啦，卡片悄悄乱了。听一听，把它们排好。",
    promptAudio: "/audio/chinese/story-sequence/4.ogg",
    assets: [],
    payload: {
      narration: "小雨来到教室，先笑着向老师问好。她把书包放好，再坐到自己的座位上。",
      narrationAudio: "/audio/chinese/story-sequence/narration-4.ogg",
      items: [
        { id: "sit-down", label: "坐到座位上" },
        { id: "say-hello", label: "向老师问好" },
        { id: "enter-classroom", label: "走进教室" },
        { id: "put-away-bag", label: "放好书包" }
      ],
      correctOrder: [
        "enter-classroom",
        "say-hello",
        "put-away-bag",
        "sit-down"
      ],
      hint: "从走进教室开始，跟着小雨一步一步往后排。"
    }
  },
  {
    id: "chinese-story-sequence-5",
    domain: "chinese",
    activityType: "story-sequence",
    difficulty: 5,
    objectives: ["理解天气变化中的行动顺序", "记住四个前后相连的情节"],
    title: "下雨回家",
    instruction: "小故事讲完啦，卡片悄悄乱了。听一听，把它们排好。",
    promptAudio: "/audio/chinese/story-sequence/5.ogg",
    assets: [],
    payload: {
      narration: "滴答滴答，下雨啦。点点撑开雨伞走回家，到家后把小伞放好晾干。",
      narrationAudio: "/audio/chinese/story-sequence/narration-5.ogg",
      items: [
        { id: "dry-umbrella", label: "把伞放好晾干" },
        { id: "walk-home", label: "撑伞走回家" },
        { id: "open-umbrella", label: "撑开雨伞" },
        { id: "see-rain", label: "看见下雨" }
      ],
      correctOrder: [
        "see-rain",
        "open-umbrella",
        "walk-home",
        "dry-umbrella"
      ],
      hint: "是什么让点点撑起了雨伞？先把那张卡片找出来。"
    }
  },
  {
    id: "chinese-story-sequence-6",
    domain: "chinese",
    activityType: "story-sequence",
    difficulty: 6,
    objectives: ["听懂包含安全做法的短故事", "独立整理四个故事步骤"],
    title: "一起做果盘",
    instruction: "小故事讲完啦，卡片悄悄乱了。听一听，把它们排好。",
    promptAudio: "/audio/chinese/story-sequence/6.ogg",
    assets: [],
    payload: {
      narration: "米米把水果洗得亮晶晶，请大人帮忙切开，再装进盘子里，和大家一起分享。",
      narrationAudio: "/audio/chinese/story-sequence/narration-6.ogg",
      items: [
        { id: "share-fruit", label: "和大家分享" },
        { id: "plate-fruit", label: "把水果放进盘子" },
        { id: "wash-fruit", label: "洗好水果" },
        { id: "ask-adult", label: "请大人帮忙切开" }
      ],
      correctOrder: [
        "wash-fruit",
        "ask-adult",
        "plate-fruit",
        "share-fruit"
      ],
      hint: "亮晶晶的水果是从哪一步开始的？找到它，再往后排。"
    }
  }
];
